import type { ChecklistItemData } from '@/api/services/boards.service'

/**
 * Client mirror of the backend sub-item rules, used for optimistic updates:
 * one level of nesting, a parent's completion is derived from its sub-items,
 * ticking a parent ticks its sub-items, and only leaf items are counted.
 */

export interface ChecklistNode {
  item: ChecklistItemData
  children: ChecklistItemData[]
}

const byPosition = (a: ChecklistItemData, b: ChecklistItemData) => a.position - b.position

/** Top-level items in order, each with its sub-items in order. */
export function buildChecklistTree(items: ChecklistItemData[]): ChecklistNode[] {
  const children = new Map<string, ChecklistItemData[]>()
  for (const item of items) {
    if (!item.parentItemId) continue
    const list = children.get(item.parentItemId) ?? []
    list.push(item)
    children.set(item.parentItemId, list)
  }
  return items
    .filter((item) => !item.parentItemId)
    .sort(byPosition)
    .map((item) => ({ item, children: (children.get(item._id) ?? []).sort(byPosition) }))
}

/** Leaf items only: a parent with sub-items is a heading, not a unit of work. */
export function countChecklistLeaves(items: ChecklistItemData[]): {
  total: number
  completed: number
} {
  const parents = new Set(items.map((item) => item.parentItemId).filter(Boolean))
  const leaves = items.filter((item) => !parents.has(item._id))
  return { total: leaves.length, completed: leaves.filter((item) => item.isCompleted).length }
}

/** Re-derives each parent's completion from its sub-items. */
function deriveParents(items: ChecklistItemData[]): ChecklistItemData[] {
  const open = new Map<string, boolean>()
  for (const item of items) {
    if (!item.parentItemId) continue
    open.set(item.parentItemId, (open.get(item.parentItemId) ?? false) || !item.isCompleted)
  }
  return items.map((item) =>
    open.has(item._id) ? { ...item, isCompleted: !open.get(item._id) } : item
  )
}

export function toggleChecklistItem(
  items: ChecklistItemData[],
  itemId: string,
  isCompleted: boolean
): ChecklistItemData[] {
  const next = items.map((item) =>
    item._id === itemId || item.parentItemId === itemId ? { ...item, isCompleted } : item
  )
  return deriveParents(next)
}

export function addChecklistItem(
  items: ChecklistItemData[],
  item: ChecklistItemData
): ChecklistItemData[] {
  return deriveParents([...items, item])
}

export function removeChecklistItem(
  items: ChecklistItemData[],
  itemId: string
): ChecklistItemData[] {
  return deriveParents(items.filter((item) => item._id !== itemId && item.parentItemId !== itemId))
}
